import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Grid, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, TextField, FormControl, InputLabel, Select,
    MenuItem, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
    Tabs, Tab, Card, CardContent
} from '@mui/material';
import { AttachMoney, Download, Print, Visibility, Search, TrendingUp } from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'react-toastify';
import api from '../services/api';

const COLORS = ['#2196f3', '#4caf50', '#ff9800', '#f44336', '#9c27b0'];

const TransportFeeReport = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [academicYears, setAcademicYears] = useState([]);
    const [classes, setClasses] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [filters, setFilters] = useState({
        academicYearId: '',
        classId: '',
        routeId: '',
        fromDate: '',
        toDate: '',
        paymentStatus: '',
        month: new Date().getMonth() + 1
    });
    const [reportData, setReportData] = useState([]);
    const [summary, setSummary] = useState({ total: 0, collected: 0, pending: 0 });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            const [yearsRes, classesRes, routesRes] = await Promise.all([
                api.get('/academic-years'),
                api.get('/classes'),
                api.get('/transport/routes').catch(() => ({ data: { data: [] } }))
            ]);
            setAcademicYears(yearsRes.data.data || []);
            setClasses(classesRes.data.data || []);
            setRoutes(routesRes.data.data || []);

            const current = yearsRes.data.data?.find(y => y.isCurrent) || yearsRes.data.data?.[0];
            if (current) setFilters(prev => ({ ...prev, academicYearId: current.id }));
        } catch (error) {
            console.error('Failed to fetch initial data');
        }
    };

    const fetchReport = async () => {
        setLoading(true);
        try {
            const reportType = ['daily', 'classwise', 'routewise', 'monthwise', 'auditor'][activeTab];
            const res = await api.get('/transport/fee-report', {
                params: { ...filters, reportType }
            });
            setReportData(res.data.data?.details || []);
            setSummary(res.data.data?.summary || { total: 0, collected: 0, pending: 0 });
        } catch (error) {
            console.error('Failed to fetch report');
            generateMockData();
        } finally {
            setLoading(false);
        }
    };

    const generateMockData = () => {
        if (activeTab === 0) { // Daily
            setReportData([
                { date: '2025-01-15', receipts: 12, amount: 24000, cash: 15000, online: 9000 },
                { date: '2025-01-14', receipts: 8, amount: 16000, cash: 10000, online: 6000 },
                { date: '2025-01-13', receipts: 15, amount: 30000, cash: 18000, online: 12000 }
            ]);
        } else if (activeTab === 1) { // Classwise
            setReportData(classes.slice(0, 8).map(c => ({
                class: c.name,
                students: Math.floor(Math.random() * 30) + 10,
                collected: Math.floor(Math.random() * 50000) + 10000,
                pending: Math.floor(Math.random() * 20000)
            })));
        } else if (activeTab === 2) { // Routewise
            setReportData([
                { route: 'Route 1 - Baramati', students: 25, fee: 2000, collected: 40000, pending: 10000 },
                { route: 'Route 2 - Supe', students: 18, fee: 1500, collected: 22500, pending: 4500 },
                { route: 'Route 3 - Pune Road', students: 30, fee: 2500, collected: 62500, pending: 12500 }
            ]);
        }
        setSummary({ total: 150000, collected: 125000, pending: 25000 });
    };

    useEffect(() => {
        if (filters.academicYearId) {
            fetchReport();
        }
    }, [activeTab]);

    const renderFilters = () => (
        <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Academic Year</InputLabel>
                        <Select
                            value={filters.academicYearId}
                            label="Academic Year"
                            onChange={e => setFilters({ ...filters, academicYearId: e.target.value })}
                        >
                            {academicYears.map(y => (
                                <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Class</InputLabel>
                        <Select
                            value={filters.classId}
                            label="Class"
                            onChange={e => setFilters({ ...filters, classId: e.target.value })}
                        >
                            <MenuItem value="">All Classes</MenuItem>
                            {classes.map(c => (
                                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Route</InputLabel>
                        <Select
                            value={filters.routeId}
                            label="Route"
                            onChange={e => setFilters({ ...filters, routeId: e.target.value })}
                        >
                            <MenuItem value="">All Routes</MenuItem>
                            {routes.map(r => (
                                <MenuItem key={r.id} value={r.id}>{r.routeName}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                    <TextField
                        fullWidth size="small" label="From Date" type="date"
                        value={filters.fromDate}
                        onChange={e => setFilters({ ...filters, fromDate: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} md={2}>
                    <TextField
                        fullWidth size="small" label="To Date" type="date"
                        value={filters.toDate}
                        onChange={e => setFilters({ ...filters, toDate: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} md={2}>
                    <Button variant="contained" startIcon={<Search />} onClick={fetchReport} fullWidth>
                        Generate
                    </Button>
                </Grid>
            </Grid>
        </Paper>
    );

    const renderSummary = () => (
        <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
                <Card sx={{ background: 'linear-gradient(135deg, #1976d2, #42a5f5)' }}>
                    <CardContent>
                        <Typography color="white" variant="body2">Total Transport Fee</Typography>
                        <Typography color="white" variant="h4">₹{summary.total.toLocaleString()}</Typography>
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12} md={4}>
                <Card sx={{ background: 'linear-gradient(135deg, #2e7d32, #66bb6a)' }}>
                    <CardContent>
                        <Typography color="white" variant="body2">Collected</Typography>
                        <Typography color="white" variant="h4">₹{summary.collected.toLocaleString()}</Typography>
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12} md={4}>
                <Card sx={{ background: 'linear-gradient(135deg, #d32f2f, #ef5350)' }}>
                    <CardContent>
                        <Typography color="white" variant="body2">Pending</Typography>
                        <Typography color="white" variant="h4">₹{summary.pending.toLocaleString()}</Typography>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );

    const renderDailyReport = () => (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Receipts</TableCell>
                        <TableCell align="right">Total Amount</TableCell>
                        <TableCell align="right">Cash</TableCell>
                        <TableCell align="right">Online</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {reportData.map((row, idx) => (
                        <TableRow key={idx}>
                            <TableCell>{row.date}</TableCell>
                            <TableCell align="right">{row.receipts}</TableCell>
                            <TableCell align="right">₹{row.amount?.toLocaleString()}</TableCell>
                            <TableCell align="right">₹{row.cash?.toLocaleString()}</TableCell>
                            <TableCell align="right">₹{row.online?.toLocaleString()}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    const renderClasswiseReport = () => (
        <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
                <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={reportData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="class" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="collected" fill="#4caf50" name="Collected" />
                        <Bar dataKey="pending" fill="#f44336" name="Pending" />
                    </BarChart>
                </ResponsiveContainer>
            </Grid>
            <Grid item xs={12} md={5}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Class</TableCell>
                                <TableCell align="right">Students</TableCell>
                                <TableCell align="right">Collected</TableCell>
                                <TableCell align="right">Pending</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {reportData.map((row, idx) => (
                                <TableRow key={idx}>
                                    <TableCell>{row.class}</TableCell>
                                    <TableCell align="right">{row.students}</TableCell>
                                    <TableCell align="right">₹{row.collected?.toLocaleString()}</TableCell>
                                    <TableCell align="right">
                                        <Chip label={`₹${row.pending?.toLocaleString()}`} size="small" color="error" />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>
        </Grid>
    );

    const renderRoutewiseReport = () => (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Route</TableCell>
                        <TableCell align="right">Students</TableCell>
                        <TableCell align="right">Monthly Fee</TableCell>
                        <TableCell align="right">Collected</TableCell>
                        <TableCell align="right">Pending</TableCell>
                        <TableCell align="right">Collection %</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {reportData.map((row, idx) => (
                        <TableRow key={idx}>
                            <TableCell>{row.route}</TableCell>
                            <TableCell align="right">{row.students}</TableCell>
                            <TableCell align="right">₹{row.fee?.toLocaleString()}</TableCell>
                            <TableCell align="right">₹{row.collected?.toLocaleString()}</TableCell>
                            <TableCell align="right">₹{row.pending?.toLocaleString()}</TableCell>
                            <TableCell align="right">
                                <Chip
                                    label={`${Math.round((row.collected / (row.collected + row.pending)) * 100)}%`}
                                    size="small"
                                    color={row.collected > row.pending ? 'success' : 'warning'}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h5">Transport Fee Reports</Typography>
                <Box>
                    <Button startIcon={<Download />} sx={{ mr: 1 }}>Export</Button>
                    <Button variant="outlined" startIcon={<Print />}>Print</Button>
                </Box>
            </Box>

            <Paper sx={{ mb: 2 }}>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable">
                    <Tab label="Daily Collection" />
                    <Tab label="Class Wise" />
                    <Tab label="Route Wise" />
                    <Tab label="Month Wise" />
                    <Tab label="Auditor Report" />
                </Tabs>
            </Paper>

            {renderFilters()}
            {renderSummary()}

            <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                    {['Daily Collection Report', 'Class Wise Report', 'Route Wise Report', 'Month Wise Report', 'Auditor Report'][activeTab]}
                </Typography>

                {activeTab === 0 && renderDailyReport()}
                {activeTab === 1 && renderClasswiseReport()}
                {activeTab === 2 && renderRoutewiseReport()}
                {activeTab === 3 && renderDailyReport()}
                {activeTab === 4 && renderDailyReport()}
            </Paper>
        </Box>
    );
};

export default TransportFeeReport;
